import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { buildAuthRedirectUrl } from "../../lib/authRedirect";

type Role = "landlord" | "tenant" | "admin";

interface ProtectedRoutesProps {
  requiredRole?: Role;
}

const roleHome: Record<string, string> = {
  landlord: "/manager/dashboard",
  tenant: "/tenant/dashboard",
  admin: "/manager/dashboard",
};

const ProtectedRoutes: React.FC<ProtectedRoutesProps> = ({ requiredRole }) => {
  const session = useAuthStore((s) => s.session);
  const roles = useAuthStore((s) => s.roles);
  const currentRole = useAuthStore((s) => s.currentRole);
  const location = useLocation();

  if (!session) {
    return (
      <Navigate to={buildAuthRedirectUrl(location)} state={{ from: location }} replace />
    );
  }

  // Role-gated route: if the user doesn't hold the required role, send them to
  // their own role home instead of a blank 403. Admins satisfy any role gate
  // (they can reach landlord/tenant areas); without this, an admin on a
  // landlord route would redirect to the same route and loop forever (blank UI).
  const satisfiesRole =
    !requiredRole || roles.includes(requiredRole) || roles.includes("admin");
  if (!satisfiesRole) {
    const homeRole = currentRole && roles.includes(currentRole) ? currentRole : roles[0];
    const fallback = homeRole ? roleHome[homeRole] : "/tenant/dashboard";
    return <Navigate to={fallback} replace />;
  }

  return <Outlet />;
};

export default ProtectedRoutes;
